import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Plane, 
  Clock, 
  MapPin, 
  Calendar,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const IMAGES = {
  // New user-provided vehicle images
  heroMain: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/ubjwu8q4_Screenshot-2024-04-28-at-7.11.00-PM.png',
  fleetLineup: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/pxgayyb3_4.jpg',
  escaladeExterior: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/zdycqsl8_IMG_9182.jpg',
  escaladeInterior: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/fztrwjm3_my26-escalade-gallery-interior-modal-steering-wheel-v2.jpeg',
  
  // Original user images
  iad: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/yfbsvpaf_iad%20airport%20dulles.jpg',
  dca: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/nbgab8mz_dca-tower.jpeg',
  terminal: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/19rc71yl_1.jpg',
  escaladeMonument: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/g0b5gai9_IMG_7208.jpg',
  dcMonument: 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/t8s41v2p_2.jpg',
  
  // DC images
  capitol: 'https://images.unsplash.com/photo-1648231150978-27af59c1d3ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxXYXNoaW5ndG9uJTIwREMlMjBDYXBpdG9sJTIwYnVpbGRpbmclMjBuaWdodHxlbnwwfHx8fDE3NzM2ODkxOTB8MA&ixlib=rb-4.1.0&q=85',
};

const services = [
  {
    id: 'airport-transfers',
    icon: Plane,
    title: 'Airport Transfers',
    subtitle: 'Seamless Airport Transportation',
    description: 'Reliable airport transportation with professional chauffeur service and luxury vehicles. Our airport transfer service provides timely pickups and comfortable rides for travelers flying in and out of the Washington DC region.',
    features: [
      'Real-time flight tracking and monitoring',
      'Meet and greet service at arrivals',
      'Complimentary wait time for flight delays',
      'Professional assistance with luggage',
      'Clean, sanitized luxury vehicles'
    ],
    image: IMAGES.iad,
    airports: [
      { code: 'IAD', name: 'Washington Dulles International Airport' },
      { code: 'DCA', name: 'Ronald Reagan National Airport' },
      { code: 'BWI', name: 'Baltimore/Washington International' }
    ]
  },
  {
    id: 'hourly-service',
    icon: Clock,
    title: 'Hourly Chauffeur Service',
    subtitle: 'Flexible Transportation On Your Schedule',
    description: 'Flexible luxury transportation with a private driver available for multiple stops or extended travel. Perfect for business meetings, city tours, or when you need transportation throughout the day.',
    features: [
      'Dedicated chauffeur for your schedule',
      'Multiple stops and flexible routing',
      'Professional driver standing by',
      'Ideal for business meetings',
      'No rushing between appointments'
    ],
    image: IMAGES.escaladeExterior
  },
  {
    id: 'city-to-city',
    icon: MapPin,
    title: 'City to City Transportation',
    subtitle: 'Long-Distance Luxury Travel',
    description: 'Private long-distance transportation between cities with luxury vehicles and professional chauffeurs. Travel in comfort without the stress of flights, rental cars, or navigating unfamiliar roads.',
    features: [
      'Door-to-door private service',
      'Comfortable long-distance travel',
      'Work or relax during your journey',
      'No flight delays or airport hassles',
      'Flexible departure times'
    ],
    destinations: [
      'New York City',
      'Philadelphia',
      'Baltimore',
      'Richmond',
      'Norfolk',
      'Atlantic City'
    ],
    image: IMAGES.dcMonument
  },
  {
    id: 'event-transportation',
    icon: Calendar,
    title: 'Event Transportation',
    subtitle: 'Special Occasion Chauffeur Service',
    description: 'Luxury chauffeur service for weddings, formal events, corporate events, and private celebrations. Make your special occasion memorable with elegant transportation and professional service.',
    features: [
      'Wedding transportation packages',
      'Corporate event coordination',
      'Formal galas and celebrations',
      'Group transportation available',
      'Customized service plans'
    ],
    image: 'https://customer-assets.emergentagent.com/job_f40a108f-f3a3-4735-ac15-18353f3eece6/artifacts/z5wwa5rc_3.jpg'
  }
];

export default function ServicesPage() {
  return (
    <div data-testid="services-page" className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={IMAGES.escaladeInterior}
            alt="Luxury Interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/95 to-[#050505]/80"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block fade-in">
            Our Services
          </span>
          <h1 
            data-testid="services-headline"
            className="text-hero font-serif text-white mb-6 max-w-3xl fade-in-delay-1"
          >
            Premium Transportation Services
          </h1>
          <p className="text-[#A3A3A3] text-lg lg:text-xl max-w-2xl leading-relaxed fade-in-delay-2">
            Comprehensive luxury ground transportation tailored to meet the 
            demands of executives and discerning travelers.
          </p>
        </div>
      </section>

      {/* Fleet Showcase */}
      <section className="section-padding bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 block">
              Our Fleet
            </span>
            <h2 className="text-3xl font-serif text-white mb-4">
              Cadillac Escalade ESV Fleet
            </h2>
            <p className="text-[#A3A3A3] max-w-2xl mx-auto">
              A versatile fleet built to meet the highest standards of executive travel.
            </p>
          </div>
          
          <div className="overflow-hidden" style={{ height: '400px' }}>
            <img
              src="https://customer-assets.emergentagent.com/job_f40a108f-f3a3-4735-ac15-18353f3eece6/artifacts/s336wmdb_Screenshot%202026-05-15%20at%209.50.24%E2%80%AFAM.png"
              alt="Capital Chauffeur Services Versatile Fleet"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Services Detail Sections */}
      {services.map((service, index) => (
        <section 
          key={service.id}
          id={service.id}
          data-testid={`service-section-${service.id}`}
          className={`section-padding ${index % 2 === 0 ? 'bg-[#050505]' : 'bg-[#0A0A0A]'}`}
        >
          <div className="max-w-7xl mx-auto">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${
              index % 2 === 1 ? 'lg:flex-row-reverse' : ''
            }`}>
              {/* Content Side */}
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 flex items-center justify-center border border-[#D4AF37]/30">
                    <service.icon className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase">
                    {service.subtitle}
                  </span>
                </div>

                <h2 className="text-section font-serif text-white mb-6">
                  {service.title}
                </h2>

                <p className="text-[#A3A3A3] text-lg leading-relaxed mb-8">
                  {service.description}
                </p>

                {/* Features List */}
                <div className="space-y-3 mb-8">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                      <span className="text-white">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Additional Info */}
                {service.airports && (
                  <div className="mb-8 p-6 bg-[#0A0A0A] border border-white/5">
                    <h4 className="text-white font-medium mb-4">Airports We Serve</h4>
                    <div className="grid grid-cols-1 gap-3">
                      {service.airports.map((airport) => (
                        <div key={airport.code} className="flex items-center gap-3">
                          <span className="px-2 py-1 bg-[#D4AF37] text-black text-xs font-medium">
                            {airport.code}
                          </span>
                          <span className="text-[#A3A3A3] text-sm">{airport.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {service.destinations && (
                  <div className="mb-8 p-6 bg-[#0A0A0A] border border-white/5">
                    <h4 className="text-white font-medium mb-4">Popular Destinations</h4>
                    <div className="flex flex-wrap gap-2">
                      {service.destinations.map((dest) => (
                        <span 
                          key={dest}
                          className="px-3 py-1.5 bg-white/5 text-[#A3A3A3] text-sm"
                        >
                          {dest}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <Link to="/book">
                  <Button 
                    data-testid={`book-${service.id}-btn`}
                    className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase inline-flex items-center gap-2"
                  >
                    Book {service.title}
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>

              {/* Image Side */}
              <div className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Decorative Element */}
                <div className={`absolute w-full h-full border-2 border-[#D4AF37]/20 -z-10 ${
                  index % 2 === 0 ? '-bottom-6 -right-6' : '-bottom-6 -left-6'
                }`}></div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section className="section-padding bg-[#050505] relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMAGES.terminal}
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/90 to-[#050505]"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-section font-serif text-white mb-6">
            Ready to Book Your{' '}
            <span className="text-[#D4AF37]">Luxury Ride</span>?
          </h2>
          <p className="text-[#A3A3A3] text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            Experience premium transportation with Capital Chauffeur Services. 
            Book online or call us directly for personalized service.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book">
              <Button 
                data-testid="services-cta-book-btn"
                className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase"
              >
                Book Now
              </Button>
            </Link>
            <a href="tel:+12026840534">
              <Button 
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5 rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase"
              >
                Call (202) 684-0534
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
